(function () {
  if (window.__cmrpCustomDropdownsInitialized) return;
  window.__cmrpCustomDropdownsInitialized = true;

  const instances = new Set();
  let openInstance = null;
  let lastViewportWidth = window.innerWidth;
  let suppressViewportCloseUntil = 0;
  let positionFrame = 0;

  function isCoarsePointer() {
    return Boolean(
      window.matchMedia &&
        window.matchMedia('(hover: none), (pointer: coarse)').matches
    );
  }

  function suppressViewportClose(ms = 800) {
    suppressViewportCloseUntil = Math.max(
      suppressViewportCloseUntil,
      Date.now() + ms
    );
  }

  function searchFieldIsFocused() {
    return Boolean(
      openInstance?.searching &&
        openInstance.searchInput &&
        document.activeElement === openInstance.searchInput
    );
  }

  function shouldCloseOnViewportChange() {
    if (!openInstance) return false;
    if (Date.now() < suppressViewportCloseUntil) return false;
    if (searchFieldIsFocused()) return false;
    if (isCoarsePointer()) return false;
    return true;
  }

  function schedulePositionOpenList() {
    if (!openInstance) return;
    if (positionFrame) return;
    positionFrame = window.requestAnimationFrame(() => {
      positionFrame = 0;
      openInstance?.positionList();
    });
  }

  function focusWithoutPageScroll(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  const TABBABLE_SELECTOR =
    'a[href], button, input, select, textarea, [tabindex]';

  function isTabStop(el) {
    if (!(el instanceof HTMLElement)) return false;
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-disabled') === 'true') return false;
    if (el.tabIndex < 0) return false;
    if (el instanceof HTMLInputElement && el.type === 'hidden') return false;
    if (el.closest('[hidden]')) return false;
    if (el.closest('[aria-hidden="true"]')) return false;
    if (el.getClientRects().length === 0) return false;
    const style = window.getComputedStyle(el);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse'
    ) {
      return false;
    }
    return true;
  }

  /**
   * Keep a focused option visible inside the portaled list (overflow-y: auto)
   * without scrolling the page. `focus({ preventScroll: true })` alone leaves
   * clipped options hidden when arrowing past the visible edge.
   */
  function scrollOptionIntoListView(list, option) {
    if (!list || !option || !list.contains(option)) return;

    const listRect = list.getBoundingClientRect();
    const optionRect = option.getBoundingClientRect();
    const pad = 4;

    if (optionRect.bottom > listRect.bottom - pad) {
      list.scrollTop += optionRect.bottom - listRect.bottom + pad;
    } else if (optionRect.top < listRect.top + pad) {
      list.scrollTop -= listRect.top + pad - optionRect.top;
    }
  }

  const SEARCH_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M6 1.33333C3.42267 1.33333 1.33333 3.42267 1.33333 6C1.33333 8.57733 3.42267 10.6667 6 10.6667C7.2573 10.6667 8.39846 10.1695 9.23759 9.36092C9.25514 9.3381 9.27436 9.31616 9.29526 9.29526C9.31616 9.27436 9.3381 9.25514 9.36092 9.23759C10.1695 8.39846 10.6667 7.2573 10.6667 6C10.6667 3.42267 8.57733 1.33333 6 1.33333ZM10.6879 9.74512C11.509 8.71868 12 7.41668 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12C7.41668 12 8.71868 11.509 9.74512 10.6879L12.1953 13.1381C12.4556 13.3984 12.8777 13.3984 13.1381 13.1381C13.3984 12.8777 13.3984 12.4556 13.1381 12.1953L10.6879 9.74512Z" fill="currentColor"/>' +
    '</svg>';

  const CHEVRON_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M0.195262 0.195262C0.455612 -0.0650874 0.877722 -0.0650874 1.13807 0.195262L4.66667 3.72386L8.19526 0.195262C8.45561 -0.0650874 8.87772 -0.0650874 9.13807 0.195262C9.39842 0.455612 9.39842 0.877722 9.13807 1.13807L5.13807 5.13807C4.87772 5.39842 4.45561 5.39842 4.19526 5.13807L0.195262 1.13807C-0.0650874 0.877722 -0.0650874 0.455612 0.195262 0.195262Z" fill="#5B5B5B"/>' +
    '</svg>';

  function optionLabel(option) {
    if (!option) return '';
    return (
      option.dataset.cmrpFullLabel ||
      option.label ||
      option.textContent ||
      ''
    ).trim();
  }

  function isPlaceholderOption(option) {
    return option.value === '';
  }

  let dropdownUid = 0;

  class CmrpCustomDropdown {
    constructor(root) {
      this.root = root;
      this.instanceId = `cmrp-dd-${++dropdownUid}`;
      this.select = root.querySelector('.cmrp-custom-dropdown__native');
      this.trigger = root.querySelector('.cmrp-custom-dropdown__trigger');
      this.value = root.querySelector('.cmrp-custom-dropdown__value');
      this.list = root.querySelector('.cmrp-custom-dropdown__list');
      this.combo = root.querySelector('.cmrp-custom-dropdown__combo') || root;
      this.label =
        root.dataset.label || this.select?.getAttribute('aria-label') || 'Select';
      this.renderedOptionsSignature = '';
      this.filterQuery = '';
      this.searching = false;
      this.searchBtn = null;
      this.searchInput = null;

      if (!this.select || !this.trigger || !this.value || !this.list) return;

      this.root.dataset.cmrpCustomDropdownReady = 'true';
      this.root.classList.add('cmrp-custom-dropdown--searchable');
      document.body.appendChild(this.list);
      this.list.setAttribute('role', 'presentation');

      this.ensureSearchUi();
      this.ensureVoiceCursor();

      this.trigger.addEventListener('click', () => {
        if (this.select.disabled) return;
        // Field click always enters type-to-filter lookup (icon is decorative only)
        if (this.searching) return;
        this.openSearch();
      });
      this.trigger.addEventListener('keydown', event =>
        this.onTriggerKeydown(event)
      );
      if (this.list.id) this.trigger.setAttribute('aria-owns', this.voiceList.id);
      // A pointer press must not focus an option inside the scroller.
      // VoiceOver would then draw its cursor from that option's layout
      // position and ignore the list's scroll offset.
      this.list.addEventListener('mousedown', event => {
        if (event.target?.closest?.('.cmrp-custom-dropdown__option')) {
          event.preventDefault();
        }
      });
      this.list.addEventListener('pointerover', event => {
        const option = event.target?.closest?.(
          '.cmrp-custom-dropdown__option'
        );
        if (!option || option.disabled || !this.list.contains(option)) return;
        this.markFocusedOption(option);
      });
      this.list.addEventListener(
        'scroll',
        () => {
          if (this.voiceOption?.isConnected) {
            this.placeVoiceCursor(this.voiceOption);
          }
        },
        { passive: true }
      );
      this.list.addEventListener('focusin', event => {
        const option = event.target?.closest?.(
          '.cmrp-custom-dropdown__option'
        );
        if (!option || option.disabled || !this.list.contains(option)) return;
        this.focusOption(option);
      });
      this.list.addEventListener('click', event => this.onOptionClick(event));
      this.list.addEventListener('keydown', event => this.onListKeydown(event));
      this.root.addEventListener('focusout', event => this.onFocusOut(event));
      this.list.addEventListener('focusout', event => this.onFocusOut(event));
      this.select.addEventListener('change', () => this.sync());

      this.observer = new MutationObserver(() => {
        this.sync();
        queueMicrotask(() => this.sync());
      });
      this.observer.observe(this.select, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });

      this.sync();
      instances.add(this);
    }

    ensureSearchUi() {
      // Remove legacy list search box if present from older builds
      this.list
        .querySelectorAll('.cmrp-custom-dropdown__search')
        .forEach(node => node.remove());

      if (!this.combo.querySelector('.cmrp-custom-dropdown__chevron')) {
        const chevron = document.createElement('span');
        chevron.className = 'cmrp-custom-dropdown__chevron';
        chevron.setAttribute('aria-hidden', 'true');
        chevron.innerHTML = CHEVRON_ICON_SVG;
        this.combo.appendChild(chevron);
      }

      if (!this.combo.querySelector('.cmrp-custom-dropdown__search-btn')) {
        const icon = document.createElement('span');
        icon.className = 'cmrp-custom-dropdown__search-btn';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = SEARCH_ICON_SVG;
        this.combo.appendChild(icon);
        this.searchBtn = icon;
      } else {
        this.searchBtn = this.combo.querySelector(
          '.cmrp-custom-dropdown__search-btn'
        );
        // Legacy builds used a clickable <button>; neutralize if still present
        if (this.searchBtn instanceof HTMLButtonElement) {
          this.searchBtn.type = 'button';
          this.searchBtn.tabIndex = -1;
          this.searchBtn.setAttribute('aria-hidden', 'true');
          this.searchBtn.removeAttribute('aria-label');
          this.searchBtn.onclick = event => {
            event.preventDefault();
            event.stopPropagation();
          };
        } else {
          this.searchBtn.setAttribute('aria-hidden', 'true');
          this.searchBtn.removeAttribute('aria-label');
        }
      }

      if (!this.combo.querySelector('.cmrp-custom-dropdown__inline-search')) {
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'cmrp-custom-dropdown__inline-search';
        input.setAttribute('autocomplete', 'off');
        input.setAttribute('autocapitalize', 'off');
        input.setAttribute('spellcheck', 'false');
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-haspopup', 'listbox');
        input.setAttribute('aria-expanded', 'false');
        if (this.list.id) input.setAttribute('aria-controls', this.list.id);
        input.setAttribute('aria-label', `Search ${this.label}`.trim());
        input.setAttribute('aria-autocomplete', 'list');
        input.placeholder = this.label;
        input.tabIndex = -1;
        input.addEventListener('input', () => {
          this.filterQuery = input.value || '';
          this.renderedOptionsSignature = '';
          if (!this.isOpen()) this.open(null, { keepSearch: true });
          else {
            this.renderOptions({ force: true });
            this.positionList();
          }
        });
        input.addEventListener('keydown', event =>
          this.onSearchKeydown(event)
        );
        input.addEventListener('click', event => event.stopPropagation());
        this.combo.appendChild(input);
        this.searchInput = input;
      } else {
        this.searchInput = this.combo.querySelector(
          '.cmrp-custom-dropdown__inline-search'
        );
      }
    }

    isOpen() {
      return openInstance === this;
    }

    containsFocus(node) {
      if (!node || !(node instanceof Node)) return false;
      return (
        this.root.contains(node) ||
        this.list.contains(node) ||
        Boolean(this.voiceList?.contains(node))
      );
    }

    onFocusOut(event) {
      if (!this.isOpen()) return;
      if (this.containsFocus(event.relatedTarget)) return;
      window.setTimeout(() => {
        if (!this.isOpen()) return;
        if (this.containsFocus(document.activeElement)) return;
        this.close();
      }, 100);
    }

    setSearching(active) {
      this.searching = Boolean(active);
      this.root.classList.toggle('is-searching', this.searching);
      if (this.searchInput) {
        this.searchInput.tabIndex = this.searching ? 0 : -1;
        if (!this.searching) {
          this.searchInput.value = '';
        }
      }
      if (this.searching && this.searchInput) {
        this.searchInput.placeholder = this.label;
      }
    }

    sync() {
      const selectedOption = this.select.options[this.select.selectedIndex];
      const hasSelectedValue = Boolean(
        selectedOption && !isPlaceholderOption(selectedOption)
      );
      const selectedLabel = hasSelectedValue
        ? optionLabel(selectedOption) || this.label
        : this.label;

      // While searching, the inline input owns the visible label text
      if (!this.searching) {
        this.value.textContent = selectedLabel;
        this.trigger.classList.toggle(
          'cmrp-custom-dropdown__trigger--placeholder',
          !hasSelectedValue
        );
      }

      this.trigger.disabled = this.select.disabled;
      this.trigger.setAttribute('aria-disabled', String(this.select.disabled));
      this.trigger.setAttribute(
        'aria-label',
        `${this.label}: ${selectedLabel}. Type to search, or use arrow keys to choose an option.`
      );
      if (this.searchBtn instanceof HTMLButtonElement) {
        this.searchBtn.disabled = this.select.disabled;
        this.searchBtn.setAttribute(
          'aria-disabled',
          String(this.select.disabled)
        );
      }

      if (this.select.disabled && this.isOpen()) {
        this.close();
        return;
      }

      if (this.isOpen()) {
        if (this.renderOptions()) this.positionList();
      }
    }

    optionsSignature() {
      return JSON.stringify({
        selectedIndex: this.select.selectedIndex,
        filter: this.filterQuery.trim().toLowerCase(),
        options: Array.from(this.select.options).map(option => [
          option.value,
          optionLabel(option),
          option.disabled,
        ]),
      });
    }

    renderOptions(opts = {}) {
      const signature = this.optionsSignature();
      if (!opts.force && signature === this.renderedOptionsSignature) {
        return false;
      }

      const activeIndex = this.list
        .querySelector('.cmrp-custom-dropdown__option.is-focused')
        ?.dataset.optionIndex;
      const filter = this.filterQuery.trim().toLowerCase();
      this.list.replaceChildren();

      let visibleCount = 0;
      Array.from(this.select.options).forEach((option, index) => {
        if (isPlaceholderOption(option)) return;
        const label = optionLabel(option);
        if (filter && !label.toLowerCase().includes(filter)) return;

        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'cmrp-custom-dropdown__option';
        item.id = `${this.instanceId}-opt-${index}`;
        item.dataset.optionIndex = String(index);
        item.tabIndex = -1;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute(
          'aria-selected',
          String(index === this.select.selectedIndex)
        );
        item.disabled = option.disabled;
        item.textContent = label;
        this.list.appendChild(item);
        visibleCount += 1;
      });

      if (!visibleCount) {
        const empty = document.createElement('div');
        empty.className = 'cmrp-custom-dropdown__empty';
        empty.textContent = filter ? 'No matches' : 'No options';
        this.list.appendChild(empty);
      }

      this.renderedOptionsSignature = signature;
      if (this.isOpen() && activeIndex != null && !this.searching) {
        const restore = this.list.querySelector(
          `[data-option-index="${activeIndex}"]`
        );
        if (restore && !restore.disabled) this.focusOption(restore);
      }
      return true;
    }

    positionAnchor() {
      return (
        this.root.closest('.cmrp-cb-control') ||
        this.root.closest('.cmrp-student-select-with-icon--custom-dropdown') ||
        this.trigger
      );
    }

    /**
     * Bottom of the sticky site header in layout-viewport coordinates.
     * Android Chrome keeps the header in the visual viewport when the keyboard
     * opens (`visualViewport.offsetTop` stays 0), so pinning to offsetTop jams
     * the field under the header. iOS often pans/collapses that chrome, which
     * is why the same 8px offset looked fine there.
     */
    stickyHeaderBottom() {
      const header =
        document.querySelector('.header-section') ||
        document.querySelector('#header-component');
      const vvTop = window.visualViewport?.offsetTop || 0;
      if (!header) return vvTop;
      const bottom = header.getBoundingClientRect().bottom;
      if (bottom <= vvTop) return vvTop;
      return bottom;
    }

    pinAnchorToViewportTop() {
      if (!isCoarsePointer()) return;
      const el = this.positionAnchor();
      if (!el) return;
      const padding = 8;
      const topEdge = this.stickyHeaderBottom();
      const rect = el.getBoundingClientRect();
      const delta = rect.top - topEdge - padding;
      if (Math.abs(delta) < 1) return;
      const scrollingEl = document.scrollingElement || document.documentElement;
      scrollingEl.scrollTop += delta;
    }

    positionList() {
      if (!this.isOpen()) return;

      const rect = this.positionAnchor().getBoundingClientRect();
      const parent = this.list.offsetParent;
      const viewportPadding = 8;
      const viewportWidth = window.innerWidth;
      const width = Math.min(
        rect.width,
        viewportWidth - viewportPadding * 2
      );
      const viewLeft = Math.min(
        Math.max(rect.left, viewportPadding),
        viewportWidth - width - viewportPadding
      );

      let top;
      let left;
      if (
        parent &&
        parent !== document.body &&
        parent !== document.documentElement
      ) {
        const parentRect = parent.getBoundingClientRect();
        top = rect.bottom + 2 - parentRect.top + (parent.scrollTop || 0);
        left = viewLeft - parentRect.left + (parent.scrollLeft || 0);
      } else {
        const scrollX = window.scrollX || document.documentElement.scrollLeft || 0;
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
        top = rect.bottom + 2 + scrollY;
        left = viewLeft + scrollX;
      }

      this.list.style.width = `${width}px`;
      this.list.style.left = `${left}px`;
      this.list.style.top = `${top}px`;
      this.list.style.bottom = 'auto';
      this.list.style.marginTop = '0';

      const vv = window.visualViewport;
      const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const spaceBelow = Math.max(
        72,
        visibleBottom - rect.bottom - viewportPadding
      );
      this.list.style.maxHeight = `${Math.min(240, spaceBelow)}px`;
      if (this.voiceOption?.isConnected) this.placeVoiceCursor(this.voiceOption);
    }

    clearFilter() {
      this.filterQuery = '';
      if (this.searchInput) this.searchInput.value = '';
      this.renderedOptionsSignature = '';
    }

    open(focusDirection, { keepSearch = false, focusSearch = false } = {}) {
      if (this.select.disabled) return;
      if (openInstance && openInstance !== this) openInstance.close();

      openInstance = this;
      this.ensureSearchUi();
      if (!keepSearch && !focusSearch) {
        this.setSearching(false);
        this.clearFilter();
      }
      this.renderOptions({ force: true });
      this.root.classList.add('is-open');
      this.list.classList.add('is-open');
      this.list.setAttribute('aria-hidden', 'false');
      this.list.scrollTop = 0;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.searchInput?.setAttribute('aria-expanded', 'true');
      suppressViewportClose(1000);
      this.pinAnchorToViewportTop();
      this.positionList();
      const scrollActiveOption = Boolean(focusDirection) && !focusSearch;
      window.requestAnimationFrame(() => {
        if (openInstance !== this) return;
        if (!scrollActiveOption) this.list.scrollTop = 0;
        this.positionList();
      });

      if (focusSearch && this.searchInput) {
        this.setSearching(true);
        focusWithoutPageScroll(this.searchInput);
        try {
          this.searchInput.select();
        } catch (e) {
          /* ignore */
        }
        return;
      }

      if (focusDirection) {
        const options = this.enabledOptionButtons();
        if (!options.length) return;

        const selected = options.find(
          option => option.getAttribute('aria-selected') === 'true'
        );
        // A new field has no selection. Arrow Up must not wrap to the last option.
        this.focusOption(selected || options[0]);
      }
    }

    /**
     * Next or previous tab stop relative to the trigger, ignoring this
     * control's own chrome and the portaled list (which lives at the end of
     * document.body and would otherwise wrap Tab back to the top of the page).
     */
    tabTargetFromTrigger(backward) {
      const skipRoots = [this.list, this.root];
      const nodes = Array.from(
        document.querySelectorAll(TABBABLE_SELECTOR)
      ).filter(el => {
        if (!(el instanceof HTMLElement)) return false;
        if (el !== this.trigger && skipRoots.some(root => root?.contains(el))) {
          return false;
        }
        return isTabStop(el);
      });
      const index = nodes.indexOf(this.trigger);
      if (index === -1) return null;
      return nodes[backward ? index - 1 : index + 1] || null;
    }

    exitOnTab(event) {
      if (event.key !== 'Tab') return false;
      event.preventDefault();
      const target = this.tabTargetFromTrigger(event.shiftKey);
      this.close(false);
      if (target) target.focus();
      else focusWithoutPageScroll(this.trigger);
      return true;
    }

    openSearch() {
      this.setSearching(true);
      this.clearFilter();
      if (this.searchInput) {
        this.searchInput.value = '';
        this.searchInput.placeholder = this.label;
      }
      if (this.isOpen()) {
        this.renderOptions({ force: true });
        this.positionList();
        suppressViewportClose();
        focusWithoutPageScroll(this.searchInput);
        try {
          this.searchInput?.select();
        } catch (e) {
          /* ignore */
        }
        return;
      }
      this.open(null, { focusSearch: true, keepSearch: true });
    }

    close(restoreFocus = false) {
      this.root.classList.remove('is-open');
      this.list.classList.remove('is-open');
      this.list.setAttribute('aria-hidden', 'true');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.searchInput?.setAttribute('aria-expanded', 'false');
      this.clearActiveOption();
      this.list.scrollTop = 0;
      this.setSearching(false);
      this.clearFilter();
      this.sync();
      if (openInstance === this) openInstance = null;
      if (restoreFocus) focusWithoutPageScroll(this.trigger);
    }

    clearActiveOption() {
      this.list
        ?.querySelectorAll('.cmrp-custom-dropdown__option.is-focused')
        .forEach(el => {
          el.classList.remove('is-focused');
          el.tabIndex = -1;
        });
      this.voiceOption = null;
      this.hideVoiceCursor();
      this.trigger?.removeAttribute('aria-activedescendant');
      this.searchInput?.removeAttribute('aria-activedescendant');
      this.list?.removeAttribute('aria-activedescendant');
    }

    ensureVoiceCursor() {
      if (this.voiceList) return;
      const list = document.createElement('div');
      list.id = `${this.instanceId}-voice-list`;
      list.className = 'cmrp-custom-dropdown__voice-list';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', this.label);
      list.hidden = true;
      this.voiceIndex = 0;
      this.voiceButtons = [0, 1].map(index => {
        const button = document.createElement('button');
        button.type = 'button';
        button.id = `${this.instanceId}-voice-${index}`;
        button.className = 'cmrp-custom-dropdown__voice-cursor';
        button.setAttribute('role', 'option');
        button.tabIndex = -1;
        button.hidden = true;
        button.addEventListener('keydown', event =>
          this.onVoiceCursorKeydown(event)
        );
        button.addEventListener('focusout', event => this.onFocusOut(event));
        list.appendChild(button);
        return button;
      });
      document.body.appendChild(list);
      this.voiceList = list;
      this.trigger?.setAttribute('aria-controls', list.id);
      this.searchInput?.setAttribute('aria-controls', list.id);
    }

    hideVoiceCursor() {
      this.voiceList?.setAttribute('hidden', '');
      if (this.voiceList) this.voiceList.hidden = true;
      this.voiceButtons?.forEach(button => {
        button.hidden = true;
        button.tabIndex = -1;
      });
    }

    /**
     * VoiceOver draws its cursor from layout position and does not subtract
     * overflow scroll. The highlighted option therefore cannot be the focused
     * node once it has scrolled. This listbox sits on that row, outside the
     * scroller, and receives focus instead.
     */
    placeVoiceCursor(option, { announce = false } = {}) {
      if (!option?.isConnected || !this.isOpen()) {
        this.hideVoiceCursor();
        return null;
      }

      const listRect = this.list.getBoundingClientRect();
      const rect = option.getBoundingClientRect();
      let top = Math.max(rect.top, listRect.top);
      let bottom = Math.min(rect.bottom, listRect.bottom);
      let height = bottom - top;
      if (height < 4 || rect.width < 4) {
        const above = rect.bottom <= listRect.top;
        top = above ? listRect.top : Math.max(listRect.top, listRect.bottom - 4);
        height = Math.min(4, listRect.height);
      }

      this.ensureVoiceCursor();
      const nextIndex = announce ? 1 - this.voiceIndex : this.voiceIndex;
      const button = this.voiceButtons[nextIndex];
      const label = (option.textContent || '').trim();
      button.hidden = false;
      button.textContent = label;
      button.setAttribute('aria-label', label);
      button.setAttribute(
        'aria-selected',
        option.getAttribute('aria-selected') || 'false'
      );
      const options = this.enabledOptionButtons();
      const index = options.indexOf(option);
      if (index >= 0) {
        button.setAttribute('aria-posinset', String(index + 1));
        button.setAttribute('aria-setsize', String(options.length));
      }
      this.voiceList.hidden = false;
      this.voiceList.style.top = `${top}px`;
      this.voiceList.style.left = `${rect.left}px`;
      this.voiceList.style.width = `${rect.width}px`;
      this.voiceList.style.height = `${height}px`;
      if (announce) this.voiceIndex = nextIndex;
      this.voiceOption = option;
      return button;
    }

    focusVoiceCursor(option) {
      const announce = option !== this.voiceOption;
      const button = this.placeVoiceCursor(option, { announce });
      if (!button) return;
      this.voiceButtons?.forEach(candidate => {
        candidate.tabIndex = candidate === button ? 0 : -1;
      });
      if (document.activeElement !== button) {
        this.movingFieldFocus = true;
        focusWithoutPageScroll(button);
        this.movingFieldFocus = false;
      }
      this.voiceButtons?.forEach(candidate => {
        if (candidate !== button) candidate.hidden = true;
      });
    }

    markFocusedOption(option) {
      if (!option) return;
      this.list
        ?.querySelectorAll('.cmrp-custom-dropdown__option.is-focused')
        .forEach(el => {
          if (el === option) return;
          el.classList.remove('is-focused');
          el.tabIndex = -1;
        });
      option.classList.add('is-focused');
      option.tabIndex = -1;
    }

    focusOption(option) {
      if (!option || option.disabled || this.movingFieldFocus) return;
      this.markFocusedOption(option);
      scrollOptionIntoListView(this.list, option);
      this.focusVoiceCursor(option);
    }

    activeOption() {
      return (
        this.list?.querySelector(
          '.cmrp-custom-dropdown__option.is-focused:not(:disabled)'
        ) || null
      );
    }

    commitActiveOption() {
      const option = this.activeOption() || this.enabledOptionButtons()[0];
      if (!option) return;
      option.click();
    }

    enabledOptionButtons() {
      return Array.from(
        this.list.querySelectorAll(
          '.cmrp-custom-dropdown__option:not(:disabled)'
        )
      );
    }

    moveOptionFocus(current, direction) {
      const options = this.enabledOptionButtons();
      if (!options.length) return;

      if (direction === 'last') {
        this.focusOption(options[options.length - 1]);
        return;
      }
      if (direction === 'first') {
        this.focusOption(options[0]);
        return;
      }

      const currentIndex = options.indexOf(current);
      if (currentIndex < 0) return;
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= options.length) return;
      this.focusOption(options[nextIndex]);
    }

    handleTypeahead(event) {
      if (
        event.key.length !== 1 ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      ) {
        return false;
      }

      event.preventDefault();
      this.setSearching(true);
      if (!this.isOpen()) {
        this.open(null, { focusSearch: true, keepSearch: true });
      } else {
        focusWithoutPageScroll(this.searchInput);
      }
      this.voiceOption = null;
      this.hideVoiceCursor();

      if (this.searchInput) {
        const next = `${this.searchInput.value || ''}${event.key}`;
        this.searchInput.value = next;
        this.filterQuery = next;
        this.renderedOptionsSignature = '';
        this.renderOptions({ force: true });
        this.positionList();
      }
      return true;
    }

    moveFromActiveOption(direction) {
      const current = this.activeOption();
      if (!current) {
        const options = this.enabledOptionButtons();
        const target =
          direction === 'last' ? options[options.length - 1] : options[0];
        if (target) this.focusOption(target);
        return;
      }
      this.moveOptionFocus(current, direction);
    }

    onSearchKeydown(event) {
      if (this.exitOnTab(event)) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveFromActiveOption(event.key === 'ArrowDown' ? 1 : -1);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        this.commitActiveOption();
      }
    }

    onTriggerKeydown(event) {
      if (this.handleTypeahead(event)) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!this.isOpen()) {
          this.open('first');
          return;
        }
        this.moveFromActiveOption(event.key === 'ArrowDown' ? 1 : -1);
      } else if ((event.key === 'Enter' || event.key === ' ') && this.isOpen()) {
        event.preventDefault();
        this.commitActiveOption();
      } else if (event.key === 'Escape' && this.isOpen()) {
        event.preventDefault();
        this.close();
      }
    }

    onListKeydown(event) {
      if (this.exitOnTab(event)) return;
      const option = event.target.closest('.cmrp-custom-dropdown__option');
      if (!option) return;

      if (this.handleTypeahead(event)) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveOptionFocus(option, event.key === 'ArrowDown' ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.moveOptionFocus(option, event.key === 'Home' ? 'first' : 'last');
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        option.click();
      }
    }

    onVoiceCursorKeydown(event) {
      if (this.exitOnTab(event)) return;
      const option = this.activeOption();
      if (!option) return;

      if (this.handleTypeahead(event)) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveOptionFocus(option, event.key === 'ArrowDown' ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.moveOptionFocus(option, event.key === 'Home' ? 'first' : 'last');
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        option.click();
      }
    }

    onOptionClick(event) {
      const option = event.target.closest('.cmrp-custom-dropdown__option');
      if (!option || option.disabled) return;

      const index = Number(option.dataset.optionIndex);
      if (!Number.isInteger(index) || !this.select.options[index]) return;

      this.select.selectedIndex = index;
      this.close(true);
      this.sync();
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    destroy() {
      if (this.isOpen()) this.close();
      this.observer?.disconnect();
      this.list?.remove();
      this.voiceList?.remove();
      this.searchBtn?.remove();
      this.searchInput?.remove();
      instances.delete(this);
      if (this.root) {
        delete this.root.dataset.cmrpCustomDropdownReady;
        this.root.classList.remove(
          'cmrp-custom-dropdown--searchable',
          'is-searching'
        );
      }
    }
  }

  function initialize(root = document) {
    root
      .querySelectorAll(
        '[data-cmrp-custom-dropdown]:not([data-cmrp-custom-dropdown-ready])'
      )
      .forEach(element => new CmrpCustomDropdown(element));
  }

  function cleanupDetached() {
    Array.from(instances).forEach(instance => {
      if (!instance.root?.isConnected) instance.destroy();
    });
  }

  function isOutsideOpenDropdown(target) {
    if (!openInstance || !(target instanceof Node)) return false;
    return (
      !openInstance.root.contains(target) &&
      !openInstance.list.contains(target) &&
      !openInstance.voiceList?.contains(target)
    );
  }

  function closeIfPointerOutside(event) {
    if (isOutsideOpenDropdown(event.target)) {
      openInstance.close();
    }
  }

  function eventIsTouch(event) {
    return event.pointerType === 'touch' || event.pointerType === 'pen';
  }

  // A touch that starts outside the list is a tap only if the finger does not
  // move. Closing on touchstart was dismissing the menu when the page scrolled.
  const OUTSIDE_TAP_SLOP_PX = 10;
  let outsideTouch = null;
  let suppressOutsideClick = false;

  document.addEventListener(
    'pointerdown',
    event => {
      if (!openInstance) return;
      if (!eventIsTouch(event)) {
        closeIfPointerOutside(event);
        return;
      }
      outsideTouch = isOutsideOpenDropdown(event.target)
        ? {
            id: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            moved: false,
          }
        : null;
    },
    true
  );

  document.addEventListener(
    'pointermove',
    event => {
      if (!outsideTouch || event.pointerId !== outsideTouch.id) return;
      const dx = event.clientX - outsideTouch.x;
      const dy = event.clientY - outsideTouch.y;
      if (dx * dx + dy * dy > OUTSIDE_TAP_SLOP_PX * OUTSIDE_TAP_SLOP_PX) {
        outsideTouch.moved = true;
      }
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'pointerup',
    event => {
      if (!outsideTouch || event.pointerId !== outsideTouch.id) return;
      const moved = outsideTouch.moved;
      outsideTouch = null;
      if (moved) {
        suppressOutsideClick = true;
        window.setTimeout(() => {
          suppressOutsideClick = false;
        }, 400);
        return;
      }
      if (!isOutsideOpenDropdown(event.target)) return;
      openInstance?.close();
    },
    true
  );

  document.addEventListener(
    'pointercancel',
    event => {
      if (outsideTouch && event.pointerId === outsideTouch.id) outsideTouch = null;
    },
    true
  );

  document.addEventListener('click', event => {
    if (suppressOutsideClick) {
      suppressOutsideClick = false;
      return;
    }
    if (eventIsTouch(event)) return;
    closeIfPointerOutside(event);
  });

  window.addEventListener('resize', () => {
    if (!openInstance) {
      lastViewportWidth = window.innerWidth;
      return;
    }
    const widthChanged = window.innerWidth !== lastViewportWidth;
    lastViewportWidth = window.innerWidth;
    if (widthChanged && shouldCloseOnViewportChange()) {
      openInstance.close();
      return;
    }
    schedulePositionOpenList();
  });

  window.addEventListener(
    'scroll',
    event => {
      if (!openInstance) return;
      if (
        event.target instanceof Node &&
        openInstance.list.contains(event.target)
      ) {
        return;
      }
      if (!shouldCloseOnViewportChange()) {
        schedulePositionOpenList();
        return;
      }
      openInstance.close();
    },
    true
  );

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', () => {
      if (!openInstance) return;
      suppressViewportClose();
      schedulePositionOpenList();
    });
    window.visualViewport.addEventListener('scroll', () => {
      if (!openInstance) return;
      schedulePositionOpenList();
    });
  }
  document.addEventListener('shopify:section:load', event =>
    initialize(event.target)
  );

  window.cmrpCustomDropdowns = {
    initialize,
    cleanupDetached,
    syncAll() {
      instances.forEach(instance => instance.sync());
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }
})();
